import * as React from 'react';
import * as XLSX from 'xlsx';

export interface ParsedRow {
    [key: string]: string;
}

export interface ImportResult {
    imported: number;
    errors: string[];
}

export interface ImportState {
    rows: ParsedRow[];
    fileName: string;
    result: ImportResult | null;
    loading: boolean;
    error: string;
}

export const emptyImportState = (): ImportState => ({
    rows: [],
    fileName: '',
    result: null,
    loading: false,
    error: '',
});

export function parseExcel(data: ArrayBuffer, headerMap: Record<string, string>): ParsedRow[] {
    const workbook = XLSX.read(data, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const raw: string[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    if (raw.length < 2) return [];

    const rawHeaders = raw[0].map(String);
    const columnIndices: Array<{ idx: number; jsonKey: string }> = [];
    rawHeaders.forEach((h, idx) => {
        const trimmed = h.trim();
        if (headerMap[trimmed]) {
            columnIndices.push({ idx, jsonKey: headerMap[trimmed] });
        }
    });

    const rows: ParsedRow[] = [];
    for (let i = 1; i < raw.length; i++) {
        const values = raw[i];
        const row: ParsedRow = {};
        let hasValue = false;
        columnIndices.forEach(({ idx, jsonKey }) => {
            const val = String(values[idx] || '').trim();
            row[jsonKey] = val;
            if (val) hasValue = true;
        });
        if (hasValue) rows.push(row);
    }
    return rows;
}

export function parseCSV(text: string, headerMap: Record<string, string>): ParsedRow[] {
    const lines = text.replace(/\r/g, '').split('\n').filter(l => l.trim() !== '');
    if (lines.length < 2) return [];

    const rawHeaders = lines[0].split(';');
    const columnIndices: Array<{ idx: number; jsonKey: string }> = [];
    rawHeaders.forEach((h, idx) => {
        const trimmed = h.trim();
        if (headerMap[trimmed]) {
            columnIndices.push({ idx, jsonKey: headerMap[trimmed] });
        }
    });

    const rows: ParsedRow[] = [];
    for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(';');
        const row: ParsedRow = {};
        let hasValue = false;
        columnIndices.forEach(({ idx, jsonKey }) => {
            const val = (values[idx] || '').trim();
            row[jsonKey] = val;
            if (val) hasValue = true;
        });
        if (hasValue) rows.push(row);
    }
    return rows;
}

export function buildTableColumns(headerMap: Record<string, string>) {
    return Object.entries(headerMap).map(([label, key]) => ({
        title: label,
        dataIndex: key,
        key,
        ellipsis: true,
    }));
}

export const ExampleCSV: React.FC<{ text: string }> = ({ text }) => (
    <pre style={{
        background: '#f6f8fa',
        border: '1px solid #d9d9d9',
        borderRadius: 4,
        padding: '10px 14px',
        fontSize: 12,
        overflowX: 'auto',
        marginTop: 12,
        marginBottom: 0,
        whiteSpace: 'pre',
    }}>
        {text}
    </pre>
);
